import { useEmployeeDocumentsFolders } from "@/queries/employees/use-employee-documents.query.ts";
import {
  useDocumentFoldersList,
  useEmployeeDocumentSave,
  useOrganizationDocumentSave,
} from "@/queries/use-documents.query.ts";
import analyticsService, { analyticEvents, analyticProperties } from "@/services/analytics-service";
import { ApiDocument, ApiDocumentSavePayload, ApiFolderType } from "@/types/document.types.ts";
import { Button, Modal, ModalProps, Select, Stack, useModals } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface DocumentMoveFileModalProps extends ModalProps {
  document: ApiDocument;
  employeeId: number | null;
}

export function DocumentMoveFileModal({
  document,
  employeeId,
  ...props
}: DocumentMoveFileModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const isOrgFolderType = document.folder.type == ApiFolderType.organization;
  const [documentState, setDocumentState] = useState(document);

  const { data: employeeFolders, isLoading: isEmployeeLoading } = useEmployeeDocumentsFolders(
    employeeId ?? undefined
  );
  const { data: organizationFolders, isLoading: isOrgLoading } = useDocumentFoldersList(
    ApiFolderType.organization,
    isOrgFolderType
  );
  const folders = (isOrgFolderType ? organizationFolders : employeeFolders)?.filter(
    (folder) => folder.acl.can_edit_documents
  );
  const isFoldersLoading = isEmployeeLoading || isOrgLoading;

  const employeeDocumentSave = useEmployeeDocumentSave();
  const orgDocumentSave = useOrganizationDocumentSave();

  const onSubmit = async () => {
    try {
      if (!document.file) {
        return;
      }
      const payload: ApiDocumentSavePayload = {
        title: document.title,
        folder_id: documentState.folder.id,
        file_uuid: document.file?.uuid,
      };

      if (isOrgFolderType) {
        await orgDocumentSave.mutateAsync({
          id: document.id,
          payload: payload,
        });
      } else if (employeeId) {
        await employeeDocumentSave.mutateAsync({
          employeeId: employeeId,
          id: document.id,
          payload: payload,
        });
      }

      analyticsService.trackEvent(analyticEvents.documents.moved, {
        [analyticProperties.id]: document.id,
      });

      close(id);
    } catch {
      return;
    }
  };

  return (
    <>
      <Modal
        {...props}
        title={t("common:move_to")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <Button
              type={"submit"}
              size={"lg"}
              isLoading={employeeDocumentSave.isPending || orgDocumentSave.isPending}
              onClick={onSubmit}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <Select
          value={documentState.folder.id}
          emptyTitle={t("common:select")}
          label={t("core:folder")}
          isLoading={isFoldersLoading}
          options={folders}
          onChange={(e) => {
            setDocumentState({
              ...documentState,
              folder: folders?.find((x) => x.id == e) ?? documentState.folder,
            });
          }}
        />
      </Modal>
    </>
  );
}
